// src/features/pages/admin/ScopePolicyPage.tsx
// Org-scope Phase B — Management Scope policy. The tenant-configurable half of
// Capability×Scope: a role answers "what may this person do"; a scope policy
// answers "over WHICH population". Each assignable role has a conservative
// default (shown), which an admin may override to a wider or narrower scope.
// Gated on `user:role:assign`; tenant-scoped server-side from the session.
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Layers, Info } from 'lucide-react';
import { RequirePermission } from '@/rbac/RequirePermission';
import { useHasPermission } from '@/rbac/useRbac';
import { getApiErrorMessage } from '@/lib/apiError';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/features/shared/components/ui/select';
import EntityGrantsSection from './EntityGrantsSection';
import {
  getScopePolicy,
  saveScopePolicy,
  type ScopePolicyResponse,
  type ScopePolicyRow,
  type PolicyScope,
} from '@/services/rbacService';

const USE_DEFAULT = '__default__';

// Human labels for the scope vocabulary.
const SCOPE_LABEL: Record<PolicyScope, string> = {
  SELF: 'Self only',
  DIRECT_REPORTS: 'Direct reports',
  REPORTING_TREE: 'Reporting tree',
  DEPARTMENT: 'Department',
  BUSINESS_UNIT: 'Business unit',
  BRANCH: 'Branch',
  TENANT: 'Whole company',
  EXPLICIT: 'Assigned list',
};

const key = (kind: string, subject: string) => `${kind}:${subject}`;

const ScopePolicyInner: React.FC = () => {
  const canWrite = useHasPermission('user:role:assign');
  const [data, setData] = useState<ScopePolicyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Override selections keyed by `${subjectKind}:${subjectKey}` → PolicyScope.
  // Absent = use the role's default. Seeded from the tenant's '*' policy rows.
  const [overrides, setOverrides] = useState<Record<string, PolicyScope>>({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await getScopePolicy();
      setData(res);
      const seeded: Record<string, PolicyScope> = {};
      for (const p of res.policies) {
        if (p.permissionGroup === '*') seeded[key(p.subjectKind, p.subjectKey)] = p.scope;
      }
      setOverrides(seeded);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not load management scope policy'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  // Any policy row that is NOT a role-level '*' override is preserved verbatim on
  // save (advanced per-permission-group rows, managed via the API for now).
  const preserved = useMemo(
    () => (data?.policies ?? []).filter((p) => p.permissionGroup !== '*'),
    [data],
  );

  const dirty = useMemo(() => {
    if (!data) return false;
    const seeded: Record<string, PolicyScope> = {};
    for (const p of data.policies) {
      if (p.permissionGroup === '*') seeded[key(p.subjectKind, p.subjectKey)] = p.scope;
    }
    const a = Object.entries(overrides);
    const b = Object.entries(seeded);
    if (a.length !== b.length) return true;
    return a.some(([k, v]) => seeded[k] !== v);
  }, [overrides, data]);

  const setOverride = (kind: string, subject: string, value: string) => {
    setOverrides((prev) => {
      const next = { ...prev };
      const k = key(kind, subject);
      if (value === USE_DEFAULT) delete next[k];
      else next[k] = value as PolicyScope;
      return next;
    });
  };

  const save = async () => {
    if (!data) return;
    setSaving(true);
    try {
      const rows: ScopePolicyRow[] = [
        ...preserved,
        ...data.defaults
          .map((d) => {
            const k = key(d.subjectKind, d.subjectKey);
            const scope = overrides[k];
            if (!scope || scope === d.defaultScope) return null; // no override
            return { subjectKind: d.subjectKind, subjectKey: d.subjectKey, permissionGroup: '*', scope };
          })
          .filter((r): r is ScopePolicyRow => r !== null),
      ];
      const saved = await saveScopePolicy(rows);
      toast.success('Management scope policy saved');
      // Re-seed from what the server persisted.
      setData({ ...data, policies: saved });
      const seeded: Record<string, PolicyScope> = {};
      for (const p of saved) {
        if (p.permissionGroup === '*') seeded[key(p.subjectKind, p.subjectKey)] = p.scope;
      }
      setOverrides(seeded);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not save management scope policy'));
    } finally {
      setSaving(false);
    }
  };

  const enforcedNote = useMemo(() => {
    const partial = (data?.scopes ?? []).filter((s) => !s.fullyEnforced).map((s) => s.value);
    return partial;
  }, [data]);

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-[12px] bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700 dark:text-orange-300 shrink-0">
          <Layers className="w-5 h-5" />
        </span>
        <div>
          <h1 className="text-h2 text-charcoal">Management scope</h1>
          <p className="text-muted-foreground mt-1">
            A role decides <em>what</em> a person can do. This decides <em>whose</em> records
            they act on. Each role has a safe default — widen or narrow it only where you need to.
          </p>
        </div>
      </div>

      {enforcedNote.length > 0 && (
        <div className="flex items-start gap-2 rounded-[12px] border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            Some scopes ({enforcedNote.map((s) => SCOPE_LABEL[s]).join(', ')}) are applied
            conservatively for now — access is never widened beyond the safe default until the
            supporting org structure ships. You can still set them; they take full effect later.
          </span>
        </div>
      )}

      {loading ? (
        <div className="text-muted-foreground">Loading…</div>
      ) : !data ? (
        <div className="text-muted-foreground">No policy data.</div>
      ) : (
        <div className="rounded-[14px] border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr>
                <th className="text-left font-medium px-4 py-3">Role</th>
                <th className="text-left font-medium px-4 py-3">Default scope</th>
                <th className="text-left font-medium px-4 py-3">Effective scope</th>
              </tr>
            </thead>
            <tbody>
              {data.defaults.map((d) => {
                const k = key(d.subjectKind, d.subjectKey);
                const override = overrides[k];
                const effective = override ?? d.defaultScope;
                const isOverridden = override && override !== d.defaultScope;
                return (
                  <tr key={k} className="border-t border-border">
                    <td className="px-4 py-3">
                      <div className="text-charcoal font-medium">{d.label || d.subjectKey}</div>
                      <div className="text-xs text-muted-foreground">
                        {d.subjectKey}
                        {d.subjectKind === 'custom_role' ? ' · custom' : ''}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {SCOPE_LABEL[d.defaultScope]}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Select
                          value={override ?? USE_DEFAULT}
                          onValueChange={(v) => setOverride(d.subjectKind, d.subjectKey, v)}
                          disabled={!canWrite}
                        >
                          <SelectTrigger className="w-[200px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={USE_DEFAULT}>
                              Default ({SCOPE_LABEL[d.defaultScope]})
                            </SelectItem>
                            {(data.scopes ?? []).map((s) => (
                              <SelectItem key={s.value} value={s.value}>
                                {SCOPE_LABEL[s.value]}
                                {s.fullyEnforced ? '' : ' *'}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {isOverridden && (
                          <Badge variant="secondary" className="shrink-0">
                            {SCOPE_LABEL[effective]}
                          </Badge>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* The other half of "over which population": which companies a role or
          person may administer. Hides itself when the tenant has one entity. */}
      <EntityGrantsSection
        canEdit={canWrite}
        roleOptions={(data?.defaults ?? [])
          .filter((d) => d.subjectKind === 'role')
          .map((d) => ({ key: d.subjectKey, label: d.label || d.subjectKey }))}
      />

      {canWrite && (
        <div className="flex items-center gap-3">
          <Button onClick={save} disabled={!dirty || saving || loading}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
          {dirty && !saving && (
            <span className="text-sm text-muted-foreground">You have unsaved changes.</span>
          )}
        </div>
      )}
    </div>
  );
};

const ScopePolicyPage: React.FC = () => (
  <RequirePermission perm="user:role:assign">
    <ScopePolicyInner />
  </RequirePermission>
);

export default ScopePolicyPage;
